import { RefObject } from 'react';
import { useVirtualizer, useWindowVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import type { VirtualizerOptions } from '@tanstack/virtual-core';

interface UseTableVirtualizerHookProps {
    count: number;
    isScrollContainer: boolean;
    tableBodyRef: RefObject<HTMLElement>;
}

interface UseTableVirtualizerHook {
    (props: UseTableVirtualizerHookProps): Virtualizer<HTMLElement, Element>;
}

const DEFAULT_ITEM_HEIGHT = 80;

export const useTableVirtualizer: UseTableVirtualizerHook = ({ count, isScrollContainer, tableBodyRef }) => {
    const getScrollMargin = () => {
        if (!tableBodyRef.current) {
            return 0;
        }
        if (isScrollContainer) {
            const containerElement = tableBodyRef.current.closest<HTMLElement>('[data-qa = "table-container"]');
            return tableBodyRef?.current.offsetTop + (containerElement?.offsetTop ?? 0);
        }
        return tableBodyRef.current
            ? document.documentElement.scrollTop + tableBodyRef.current.getBoundingClientRect().top
            : 0;
    };
    const virtualizerOptions: Pick<
        VirtualizerOptions<HTMLElement, Element>,
        'count' | 'measureElement' | 'estimateSize' | 'overscan' | 'scrollMargin' | 'initialRect'
    > = {
        count,
        estimateSize: () => DEFAULT_ITEM_HEIGHT,
        overscan: 5,
        measureElement: (element) => {
            let rowHeight = element?.getBoundingClientRect().height;
            const siblingElement = element.nextElementSibling;
            if (siblingElement && siblingElement.hasAttribute('data-magritte-table-row-panel')) {
                rowHeight += siblingElement.getBoundingClientRect().height;
            }
            return rowHeight;
        },
        scrollMargin: getScrollMargin(),
        initialRect: { width: 0, height: 5 * DEFAULT_ITEM_HEIGHT },
    };

    const virtualizerHook = (isScrollContainer ? useVirtualizer : useWindowVirtualizer) as typeof useVirtualizer;

    const virtualizer = virtualizerHook({
        ...virtualizerOptions,
        ...(isScrollContainer
            ? {
                  getScrollElement: () => {
                      return tableBodyRef.current?.closest('[data-magritte-table-wrapper]') || null;
                  },
              }
            : {}),
    } as VirtualizerOptions<HTMLElement, Element>);

    return virtualizer;
};
